import { Home, Plus, Target, Wallet, BarChart3, RefreshCcw, FileText, Calendar, Users, Sparkles, Camera, Settings, Bug, Lightbulb, type LucideIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FeedbackModal } from "@/components/feedback/FeedbackModal";
import { useSubscription } from "@/hooks/useSubscription";
import { useFirstTimeUser } from "@/hooks/useFirstTimeUser";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { ScopeSwitcher } from "@/components/family/ScopeSwitcher";
import type { FamilyNavKey } from "@/lib/familyNavigation";
import type { TabType } from "@/types/navigation";

type NavItem = {
  key: FamilyNavKey;
  icon: LucideIcon;
  labelKey: string;
  tab?: TabType;
  href?: string;
};

interface DesktopSidebarProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onAddExpense: () => void;
  onScanReceipt?: () => void;
  onUploadDocument?: () => void;
  onSignOut: () => Promise<void>;
  /** Capability filter from the app shell. Defaults to allowing everything. */
  can?: (key: FamilyNavKey) => boolean;
}

const navItems: NavItem[] = [
  { key: "home", icon: Home, labelKey: "items.home", tab: "home" },
  { key: "budget", icon: Wallet, labelKey: "items.budget", tab: "budget" },
  { key: "goals", icon: Target, labelKey: "items.goals", tab: "goals" },
  { key: "calendar", icon: Calendar, labelKey: "items.calendar", tab: "calendar" },
  { key: "insights", icon: BarChart3, labelKey: "items.insights", tab: "insights" },
  { key: "settings", icon: Settings, labelKey: "items.settings", tab: "settings" },
];

const secondaryNavItems: NavItem[] = [
  { key: "recurring", icon: RefreshCcw, labelKey: "items.recurring", href: "/upcoming" },
  { key: "documents", icon: FileText, labelKey: "items.documents", href: "/documents" },
  { key: "family", icon: Users, labelKey: "items.family", href: "/family" },
  { key: "journey", icon: Sparkles, labelKey: "items.fennie", href: "/journey" },
  { key: "roadmap", icon: Lightbulb, labelKey: "items.roadmap", href: "/roadmap" },
];

/**
 * Desktop sidebar (lg+). The active view is derived from the route so the
 * highlighted item stays correct on non-Dashboard pages. Items the active
 * family role cannot reach are not rendered at all.
 */
export function DesktopSidebar({
  activeTab,
  onTabChange,
  onAddExpense,
  onScanReceipt,
  onUploadDocument,
  onSignOut,
  can,
}: DesktopSidebarProps) {
  const { t } = useTranslation("nav");
  const allow = can ?? (() => true);
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<"feature" | "bug">("feature");
  const scanReceipt = onScanReceipt ?? (() => navigate("/dashboard?add=scan"));
  const uploadDocument = onUploadDocument ?? (() => navigate("/documents"));
  const { isPremium } = useSubscription();
  const { user } = useAuth();
  const { isFirstTime } = useFirstTimeUser();

  if (isFirstTime || !user) return null;

  const primary = navItems.filter((item) => allow(item.key));
  const secondary = secondaryNavItems.filter((item) => allow(item.key));

  const handleItemClick = (item: NavItem) => {
    if (item.href) {
      navigate(item.href);
    } else if (item.tab) {
      onTabChange(item.tab);
    }
  };

  const renderItem = (item: NavItem) => {
    const isActive = item.tab ? activeTab === item.tab : false;
    const Icon = item.icon;
    return (
      <button
        key={item.key}
        onClick={() => handleItemClick(item)}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        )}
      >
        <Icon className="h-4 w-4" />
        {t(item.labelKey)}
      </button>
    );
  };

  return (
    <aside className="fixed inset-y-0 start-0 z-40 hidden w-64 flex-col border-e border-border/40 bg-background/80 px-3 py-6 backdrop-blur-xl lg:flex">
      <div className="px-2">
        <span className="font-display text-lg tracking-tight text-foreground">Fennest</span>
        <div className="mt-2">
          <ScopeSwitcher />
        </div>
      </div>

      {!isPremium && (
        <button
          onClick={() => navigate("/pricing")}
          className="mx-2 mt-4 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 text-start"
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t("desktop.freePlan")}
          </div>
          <div className="text-sm font-medium text-primary">{t("desktop.upgradeToPremium")}</div>
        </button>
      )}

      <nav className="mt-6 flex flex-1 flex-col gap-1 overflow-y-auto">
        {primary.map(renderItem)}

        <div className="my-3 border-t border-border/30" />

        <div className="px-3 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground/60">
          {t("desktop.quickActions")}
        </div>
        {allow("add") && (
          <button
            onClick={onAddExpense}
            className="flex w-full items-center gap-3 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-transform active:scale-[0.98]"
          >
            <Plus className="h-4 w-4" />
            {t("desktop.addExpense")}
          </button>
        )}
        {allow("scanner") && (
          <button
            onClick={scanReceipt}
            className="flex w-full items-center gap-3 rounded-xl border border-border/40 px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/50"
          >
            <Camera className="h-4 w-4" />
            {t("desktop.scanReceipt")}
          </button>
        )}
        {allow("documents") && (
          <button
            onClick={uploadDocument}
            className="flex w-full items-center gap-3 rounded-xl border border-border/40 px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/50"
          >
            <FileText className="h-4 w-4" />
            {t("desktop.uploadDocument")}
          </button>
        )}

        {secondary.length > 0 && <div className="my-3 border-t border-border/30" />}
        {secondary.map(renderItem)}
      </nav>

      <div className="mt-4 border-t border-border/30 pt-3">
        <div className="flex flex-col gap-1">
          <button
            onClick={() => { setFeedbackType("bug"); setFeedbackOpen(true); }}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
          >
            <Bug className="h-4 w-4" />
            {t("desktop.reportBug")}
          </button>
          <button
            onClick={() => { setFeedbackType("feature"); setFeedbackOpen(true); }}
            className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
          >
            <Lightbulb className="h-4 w-4" />
            {t("desktop.requestFeature")}
          </button>
        </div>
        <button
          onClick={onSignOut}
          className="mt-2 flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          {t("desktop.signOut")}
        </button>
      </div>
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} defaultType={feedbackType} />
    </aside>
  );
}
